export function formatError(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}

// Correlation IDs are opaque identifiers, never error descriptions, URLs or
// credentials. Reject unsafe values instead of partially displaying them.
export function sanitizeRequestId(value: unknown): string | undefined {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(value)
    ? value
    : undefined;
}

export function appendRequestId(message: string, requestId?: string | null): string {
  const safeRequestId = sanitizeRequestId(requestId);
  if (!safeRequestId || message.match(/\(request [^)]+\)/i)) {
    return message;
  }
  return `${message} (request ${safeRequestId})`;
}

export function formatWebSocketDisconnectedMessage({
  context,
  detail,
  event,
  requestId
}: {
  context: string;
  detail: string;
  event?: Pick<CloseEvent, 'code' | 'reason'> | null;
  requestId?: string | null;
}): string {
  const parts = [context];
  if (event) {
    parts.push(`code ${event.code}`);
    if (event.reason) {
      parts.push(event.reason);
    }
  }
  return appendRequestId(`${parts.join(': ')}. ${detail}`, requestId);
}

export function formatWebSocketTokenFailureMessage(context: string, err: unknown): string {
  return formatWebSocketDisconnectedMessage({
    context,
    detail: formatError(err)
  });
}

export const TERMINAL_IDENTITY_MISMATCH_ERROR_CODE = 'E_TERMINAL_IDENTITY_MISMATCH';

function errorCode(err: unknown) {
  if (!err || typeof err !== 'object' || !('code' in err)) return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

export function isTerminalIdentityMismatchError(err: unknown) {
  const message = formatError(err).toLocaleLowerCase();
  return errorCode(err) === TERMINAL_IDENTITY_MISMATCH_ERROR_CODE ||
    message.includes('different terminal identity');
}

export function formatExecutorTerminalTokenFailureMessage(
  context: string,
  err: unknown,
  canManage = true
) {
  if (!isTerminalIdentityMismatchError(err)) {
    return formatWebSocketTokenFailureMessage(context, err);
  }
  const requestId = formatError(err).match(/\(request ([^)]+)\)/i)?.[1];
  const recovery = canManage
    ? 'Restore the executor identity mapping or remove and recreate this session.'
    : 'Ask the session owner to restore the executor identity mapping or remove and recreate this session.';
  return appendRequestId(
    `${context}: this saved session is tied to a different terminal identity. ${recovery}`,
    requestId
  );
}

const TERMINAL_SESSION_LOST_MESSAGE =
  'Terminal session is no longer available because the executor was restarted or the host PTY was removed. The saved session cannot be reattached; start a new terminal.';
const TERMINAL_SESSION_EXPIRED_MESSAGE =
  'Terminal session expired because its retention period ended. Start a new terminal to continue.';

export function formatExecutorTerminalProtocolError(message: string, requestId?: string | null) {
  const normalized = message.toLocaleLowerCase();
  const sessionMissing =
    (normalized.includes('pty session') || normalized.includes('tmux session')) &&
    normalized.includes('does not exist');
  const sessionExpired = normalized.includes('retention') && normalized.includes('expired');
  const formatted = sessionMissing
    ? TERMINAL_SESSION_LOST_MESSAGE
    : sessionExpired
      ? TERMINAL_SESSION_EXPIRED_MESSAGE
      : message;
  return appendRequestId(formatted, requestId);
}

export function reportError(
  context: string,
  err: unknown,
  setError: (message: string | null) => void
) {
  const message = `${context}: ${formatError(err)}`;
  console.error(message, err);
  setError(message);
}

export function reportErrorLocal(
  context: string,
  err: unknown,
  setError: (message: string | null) => void,
  setLocalError: (message: string | null) => void
) {
  const message = `${context}: ${formatError(err)}`;
  console.error(message, err);
  setError(message);
  setLocalError(message);
}
